<template>
  <div class="concurso-formulario">
    <div class="form-header">
      <div class="header-left">
        <button @click="voltar" class="btn-voltar">
          <i class="fas fa-arrow-left"></i>
          Voltar
        </button>
        <div class="header-info">
          <h1 class="form-title">
            {{ isEdit ? 'Editar Concurso' : 'Novo Concurso' }}
          </h1>
          <p class="form-subtitle">
            {{ isEdit ? 'Atualize as informações do concurso' : 'Preencha os dados para criar um novo concurso' }}
          </p>
        </div>
      </div>
      <div class="header-right">
        <button @click="salvar" class="btn-salvar" :disabled="!isFormValid || saving">
          <i v-if="saving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>

    <div class="form-content">
      <form @submit.prevent="salvar" class="concurso-form">
        <!-- Informações Básicas -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-info-circle"></i>
              Informações Básicas
            </h3>
            <p class="section-description">Dados principais do concurso público</p>
          </div>
          
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required">
                <i class="fas fa-heading"></i>
                Título do Concurso
              </label>
              <input
                v-model="form.titulo"
                type="text"
                class="form-control"
                placeholder="Digite o título do concurso"
                required
                :class="{ 'is-invalid': errors.titulo }"
              />
              <div v-if="errors.titulo" class="invalid-feedback">
                {{ errors.titulo }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">
                <i class="fas fa-building"></i>
                Prefeitura
              </label>
              <input
                v-model="form.prefeitura"
                type="text"
                class="form-control"
                placeholder="Digite o nome da prefeitura"
                required
                :class="{ 'is-invalid': errors.prefeitura }"
              />
              <div v-if="errors.prefeitura" class="invalid-feedback">
                {{ errors.prefeitura }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">
                <i class="fas fa-tag"></i>
                Tipo de Concurso
              </label>
              <select
                v-model="form.tipo"
                class="form-select"
                required
                :class="{ 'is-invalid': errors.tipo }"
              >
                <option value="">Selecione o tipo</option>
                <option value="Concurso Público">Concurso Público</option>
                <option value="Processo Seletivo">Processo Seletivo</option>
                <option value="Seleção Pública">Seleção Pública</option>
                <option value="Concurso de Remoção">Concurso de Remoção</option>
              </select>
              <div v-if="errors.tipo" class="invalid-feedback">
                {{ errors.tipo }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-link"></i>
                Link da Área do Candidato
              </label>
              <input
                v-model="form.link_area_candidato"
                type="url"
                class="form-control"
                placeholder="https://exemplo.com/area-candidato"
                :class="{ 'is-invalid': errors.link_area_candidato }"
              />
              <div v-if="errors.link_area_candidato" class="invalid-feedback">
                {{ errors.link_area_candidato }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-align-left"></i>
              Descrição
            </label>
            <textarea
              v-model="form.descricao"
              class="form-control"
              rows="4"
              placeholder="Descreva o concurso público..."
              :class="{ 'is-invalid': errors.descricao }"
            ></textarea>
            <div v-if="errors.descricao" class="invalid-feedback">
              {{ errors.descricao }}
            </div>
          </div>
        </div>

        <!-- Período de Inscrições -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-calendar-alt"></i>
              Período de Inscrições
            </h3>
            <p class="section-description">Defina as datas de início e fim das inscrições</p>
          </div>
          
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required">
                <i class="fas fa-play"></i>
                Data de Início
              </label>
              <input
                v-model="form.data_prazo_inicio"
                type="datetime-local"
                class="form-control"
                required
                :class="{ 'is-invalid': errors.data_prazo_inicio }"
              />
              <div v-if="errors.data_prazo_inicio" class="invalid-feedback">
                {{ errors.data_prazo_inicio }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">
                <i class="fas fa-stop"></i>
                Data de Encerramento
              </label>
              <input
                v-model="form.data_prazo_fim"
                type="datetime-local"
                class="form-control"
                required
                :class="{ 'is-invalid': errors.data_prazo_fim }"
              />
              <div v-if="errors.data_prazo_fim" class="invalid-feedback">
                {{ errors.data_prazo_fim }}
              </div>
            </div>
          </div>
        </div>

        <!-- Configurações Adicionais -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-cogs"></i>
              Configurações Adicionais
            </h3>
            <p class="section-description">Configurações específicas do concurso</p>
          </div>
          
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-calendar-check"></i>
                Prazo Final para Isenções
              </label>
              <input
                v-model="form.prazo_final_isencao"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.prazo_final_isencao }"
              />
              <div v-if="errors.prazo_final_isencao" class="invalid-feedback">
                {{ errors.prazo_final_isencao }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-info-circle"></i>
                Status do Concurso
              </label>
              <select
                v-model="form.status"
                class="form-select"
                :class="{ 'is-invalid': errors.status }"
              >
                <option value="">Selecione o status</option>
                <option value="A">A - Ativo</option>
                <option value="EM ANDAMENTO">EM ANDAMENTO</option>
                <option value="Encerrado">Encerrado</option>
              </select>
              <div v-if="errors.status" class="invalid-feedback">
                {{ errors.status }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-toggle-on"></i>
                Ativo
              </label>
              <select
                v-model="form.ativo"
                class="form-select"
                :class="{ 'is-invalid': errors.ativo }"
              >
                <option :value="1">Ativo</option>
                <option :value="0">Inativo</option>
              </select>
              <div v-if="errors.ativo" class="invalid-feedback">
                {{ errors.ativo }}
              </div>
            </div>

            <div class="form-group">
              <label>Quantidade máxima de inscrições por Candidato</label>
              <input
                type="number"
                min="0"
                class="form-control"
                v-model.number="form.qtd_max_inscricoes"
                placeholder="Deixe vazio para ilimitado"
              />
            </div>

            <div class="form-group form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="exibirPontuacao"
                v-model="form.exibir_pontuacao_situacao"
              />
              <label class="form-check-label" for="exibirPontuacao">
                Exibir pontuação e situação dos (*ENVIOS / TITULOS*)
              </label>
            </div>

          </div>
        </div>

        <!-- Validação do Formulário -->
        <div class="form-validation">
          <div class="validation-header">
            <h4>
              <i class="fas fa-check-circle"></i>
              Validação do Formulário
            </h4>
          </div>
          <div class="validation-items">
            <div class="validation-item" :class="{ 'valid': form.titulo }">
              <i class="fas fa-check"></i>
              <span>Título preenchido</span>
            </div>
            <div class="validation-item" :class="{ 'valid': form.prefeitura }">
              <i class="fas fa-check"></i>
              <span>Prefeitura informada</span>
            </div>
            <div class="validation-item" :class="{ 'valid': form.tipo }">
              <i class="fas fa-check"></i>
              <span>Tipo selecionado</span>
            </div>
            <div class="validation-item" :class="{ 'valid': form.data_prazo_inicio && form.data_prazo_fim }">
              <i class="fas fa-check"></i>
              <span>Período de inscrições definido</span>
            </div>
            <div class="validation-item" :class="{ 'valid': isPeriodValid }">
              <i class="fas fa-check"></i>
              <span>Período válido (início antes do fim)</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ConcursoFormulario',
  data() {
    return {
      saving: false,
      form: {
        titulo: '',
        prefeitura: '',
        tipo: '',
        descricao: '',
        link_area_candidato: '',
        data_prazo_inicio: '',
        data_prazo_fim: '',
        prazo_final_isencao: '',
        status: '',
        ativo: 1,
        qtd_max_inscricoes: null,
        exibir_pontuacao_situacao: false,
      },
      errors: {}
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    isFormValid() {
      return this.form.titulo && 
             this.form.prefeitura && 
             this.form.tipo && 
             this.form.data_prazo_inicio && 
             this.form.data_prazo_fim &&
             this.isPeriodValid
    },
    isPeriodValid() {
      if (!this.form.data_prazo_inicio || !this.form.data_prazo_fim) return false
      return new Date(this.form.data_prazo_inicio) < new Date(this.form.data_prazo_fim)
    }
  },
  mounted() {
    if (this.isEdit) {
      this.carregarConcurso()
    }
  },
  methods: {
    async carregarConcurso() {
      try {
        const { data } = await axios.get(`/ps/concurso/${this.$route.params.id}`)
        
        // Converter ativo para número e garantir valor padrão
        let ativoValue = 1; // Valor padrão
        if (data.ativo !== undefined && data.ativo !== null) {
          // Converter para número (pode vir como string "1" ou "0", ou boolean, ou número)
          if (typeof data.ativo === 'boolean') {
            ativoValue = data.ativo ? 1 : 0;
          } else if (typeof data.ativo === 'string') {
            ativoValue = data.ativo === '1' || data.ativo === 'true' ? 1 : 0;
          } else {
            ativoValue = Number(data.ativo) || 1;
          }
        }
        
        this.form = {
          titulo: data.titulo || '',
          prefeitura: data.prefeitura || '',
          tipo: data.tipo || '',
          descricao: data.descricao || '',
          link_area_candidato: data.link_area_candidato || '',
          data_prazo_inicio: this.formatDateTimeForInput(data.data_prazo_inicio),
          data_prazo_fim: this.formatDateTimeForInput(data.data_prazo_fim),
          prazo_final_isencao: this.formatDateTimeForInput(data.prazo_final_isencao),
          status: data.status || '',
          ativo: ativoValue,
          qtd_max_inscricoes: data.qtd_max_inscricoes || null,
          exibir_pontuacao_situacao: data.exibir_pontuacao_situacao || false,
        }
        
        // Debug para verificar o valor
        // console.log('Concurso carregado - ativo original:', data.ativo, 'ativo convertido:', ativoValue, 'tipo:', typeof ativoValue)
      } catch (error) {
        console.error('Erro ao carregar concurso:', error)
        this.$sweet.showError('Erro ao carregar dados do concurso')
        this.voltar()
      }
    },
    async salvar() {
      if (!this.isFormValid) {
        this.$sweet.showWarning('Preencha todos os campos obrigatórios')
        return
      }

      this.saving = true
      this.errors = {}

      try {
        const formData = {
          ...this.form,
          data_prazo_inicio: this.convertDateTimeLocalToISO(this.form.data_prazo_inicio),
          data_prazo_fim: this.convertDateTimeLocalToISO(this.form.data_prazo_fim),
          prazo_final_isencao: this.form.prazo_final_isencao ? 
            this.convertDateTimeLocalToISO(this.form.prazo_final_isencao) : null
        }

        if (this.isEdit) {
          await axios.put(`/ps/concurso/atualizar/${this.$route.params.id}`, formData)
          this.$sweet.showSuccess('Concurso atualizado com sucesso!')
        } else {
          await axios.post('/ps/concurso/criar', formData)
          this.$sweet.showSuccess('Concurso criado com sucesso!')
        }

        this.$router.push({ name: 'admin-concursos' })
      } catch (error) {
        console.error('Erro ao salvar concurso:', error)
        
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {}
          this.$sweet.showError('Por favor, corrija os erros no formulário')
        } else {
          this.$sweet.showError('Erro ao salvar concurso')
        }
      } finally {
        this.saving = false
      }
    },
    voltar() {
      this.$router.push({ name: 'admin-concursos' })
    },
    formatDateTimeForInput(dateTime) {
      if (!dateTime) return ''
      
      // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
      if (typeof dateTime === 'string') {
        let dateString = dateTime.replace('Z', '')
        
        // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
          const [datePart, timePart] = dateString.split(' ')
          const [hours, minutes] = timePart.split(':')
          // Retornar no formato datetime-local (YYYY-MM-DDTHH:mm)
          return `${datePart}T${hours}:${minutes}`
        }
        
        // Extrair diretamente da string ISO sem usar new Date() para evitar conversão de timezone
        if (dateString.includes('T')) {
          const parts = dateString.split('T')
          if (parts.length === 2) {
            const [datePart, timePart] = parts
            // Remover informações de timezone (Z, +03:00, etc) e pegar apenas hora:minuto
            const timeClean = timePart.replace(/[Z+-].*$/, '')
            const [hours, minutes] = timeClean.split(':')
            // Retornar no formato datetime-local (YYYY-MM-DDTHH:mm)
            return `${datePart}T${hours}:${minutes}`
          }
        }
      }
      
      // Fallback: tentar usar new Date() se não for formato conhecido
      try {
        const date = new Date(dateTime)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
      } catch {
        return ''
      }
    },
    convertDateTimeLocalToISO(dateTimeLocal) {
      if (!dateTimeLocal) return null
      
      // O formato datetime-local é: "YYYY-MM-DDTHH:mm"
      // Problema: new Date().toISOString() converte para UTC, alterando o horário
      // Solução: Construir a string ISO manualmente preservando o horário exato digitado
      // Isso garante que o horário salvo seja exatamente o que o usuário digitou
      
      // O datetime-local já está no formato correto, só precisamos adicionar segundos e milissegundos
      // Formato esperado: "YYYY-MM-DDTHH:mm:ss.sssZ"
      // Vamos adicionar ":00.000" para segundos/milissegundos e "Z" para indicar UTC
      // Mas sem fazer conversão de timezone - preservando o horário exato
      
      // Se a string já tem o formato completo, usar diretamente
      if (dateTimeLocal.includes(':')) {
        const parts = dateTimeLocal.split('T')
        if (parts.length === 2) {
          const [datePart, timePart] = parts
          // Garantir que tem segundos e milissegundos
          const timeWithSeconds = timePart.includes(':') && timePart.split(':').length === 2 
            ? `${timePart}:00` 
            : timePart
          // Retornar no formato ISO preservando o horário exato
          return `${datePart}T${timeWithSeconds}.000Z`
        }
      }
      
      // Fallback: usar o método anterior se o formato não for o esperado
      const localDate = new Date(dateTimeLocal)
      const timezoneOffset = localDate.getTimezoneOffset()
      const adjustedDate = new Date(localDate.getTime() + (timezoneOffset * 60 * 1000))
      return adjustedDate.toISOString()
    }
  }
}
</script>

<style lang="scss" scoped>
.concurso-formulario {
  .form-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;

    .header-left {
      display: flex;
      align-items: center;
      gap: 20px;

      .btn-voltar {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        color: #6c757d;
        padding: 10px 15px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;

        &:hover {
          background: #e9ecef;
          color: #495057;
        }
      }

      .header-info {
        .form-title {
          margin: 0 0 5px 0;
          font-size: 24px;
          font-weight: 600;
          color: #2c3e50;
        }

        .form-subtitle {
          margin: 0;
          font-size: 14px;
          color: #6c757d;
        }
      }
    }

    .header-right {
      .btn-salvar {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
        }

        &:disabled {
          background: #6c757d;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
      }
    }
  }

  .form-content {
    background: white;
    border-radius: 15px;
    padding: 30px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .concurso-form {
    .form-section {
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        border-bottom: none;
        margin-bottom: 0;
      }

      .section-header {
        margin-bottom: 25px;

        .section-title {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          display: flex;
          align-items: center;
          gap: 10px;

          i {
            color: #667eea;
          }
        }

        .section-description {
          margin: 0;
          font-size: 14px;
          color: #6c757d;
        }
      }

      .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 20px;
        margin-bottom: 20px;
      }

      .form-group {
        .form-label {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          font-size: 14px;
          gap: 8px;

          &.required::after {
            content: '*';
            color: #dc3545;
            margin-left: 4px;
          }

          i {
            color: #667eea;
            width: 16px;
          }
        }

        .form-control,
        .form-select {
          width: 100%;
          padding: 12px 15px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;

          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }

          &.is-invalid {
            border-color: #dc3545;
          }

          &::placeholder {
            color: #6c757d;
          }
        }

        textarea.form-control {
          resize: vertical;
          min-height: 100px;
        }

        .invalid-feedback {
          display: block;
          color: #dc3545;
          font-size: 12px;
          margin-top: 5px;
        }
      }
    }
  }

  .form-validation {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 20px;
    margin-top: 30px;

    .validation-header {
      margin-bottom: 15px;

      h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #28a745;
        }
      }
    }

    .validation-items {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 10px;

      .validation-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        border-radius: 6px;
        background: white;
        border: 1px solid #e9ecef;
        transition: all 0.3s ease;

        i {
          color: #6c757d;
          font-size: 12px;
        }

        span {
          font-size: 13px;
          color: #6c757d;
        }

        &.valid {
          background: rgba(40, 167, 69, 0.1);
          border-color: #28a745;

          i {
            color: #28a745;
          }

          span {
            color: #28a745;
            font-weight: 500;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .concurso-formulario {
    .form-header {
      padding: 20px;
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;

      .header-left {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }
    }

    .form-content {
      padding: 20px;
    }

    .concurso-form {
      .form-section {
        .form-grid {
          grid-template-columns: 1fr;
        }
      }
    }
  }
}
</style>
